/**
 * ===================================================
 * Module: frontend.src.i18n.config
 * Description: i18next configuration for internationalization
 * Owner: Kawada Technosystem Co Ltd
 * All Rights Reserved
 * ===================================================
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import locale resources
import enCommon from '../locales/en/common.json';
import enAuth from '../locales/en/auth.json';
import enProjects from '../locales/en/projects.json';
import enSessions from '../locales/en/sessions.json';
import enPdfEditor from '../locales/en/pdfEditor.json';
import enNotifications from '../locales/en/notifications.json';
import enAdmin from '../locales/en/admin.json';
import enAuditLog from '../locales/en/auditLog.json';
import enTenant from '../locales/en/tenant.json';

import jaCommon from '../locales/ja/common.json';
import jaAuth from '../locales/ja/auth.json';
import jaProjects from '../locales/ja/projects.json';
import jaSessions from '../locales/ja/sessions.json';
import jaPdfEditor from '../locales/ja/pdfEditor.json';
import jaNotifications from '../locales/ja/notifications.json';
import jaAdmin from '../locales/ja/admin.json';
import jaAuditLog from '../locales/ja/auditLog.json';
import jaTenant from '../locales/ja/tenant.json';

// Determine language based on execution mode: DEV = English, PROD = Japanese
const getLanguage = (): string => {
  const executionMode = process.env.REACT_APP_EXECUTION_MODE;
  return executionMode === 'DEV' ? 'en' : 'ja';
};

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enCommon,
        auth: enAuth,
        projects: enProjects,
        sessions: enSessions,
        pdfEditor: enPdfEditor,
        notifications: enNotifications,
        admin: enAdmin,
        auditLog: enAuditLog,
        tenant: enTenant,
      },
      ja: {
        common: jaCommon,
        auth: jaAuth,
        projects: jaProjects,
        sessions: jaSessions,
        pdfEditor: jaPdfEditor,
        notifications: jaNotifications,
        admin: jaAdmin,
        auditLog: jaAuditLog,
        tenant: jaTenant,
      },
    },
    lng: getLanguage(), // Set language based on REACT_APP_EXECUTION_MODE
    defaultNS: 'common',
    fallbackLng: 'en',
    supportedLngs: ['en', 'ja'],
    interpolation: {
      escapeValue: false, // React already escapes
    },
    react: {
      useSuspense: false, // Avoid suspense for better compatibility
    },
    // Pluralization configuration
    pluralSeparator: '_',
    // Development mode: return key if translation missing
    returnEmptyString: false,
    returnNull: false,
    // Show keys in development if debug mode enabled
    debug: process.env.NODE_ENV === 'development' && process.env.REACT_APP_I18N_DEBUG === 'true',
  });

// Update the HTML lang attribute to match the current language
// This ensures browser features (like spell check, translation prompts) work correctly
const updateDocumentLanguage = (lang: string) => {
  document.documentElement.lang = lang;
  document.documentElement.setAttribute('translate', 'no');
};

// Set initial language
updateDocumentLanguage(i18n.language);

// Update when language changes (if language switching is added in the future)
i18n.on('languageChanged', updateDocumentLanguage);

export default i18n;
